import { Suspense, lazy, useEffect, useMemo, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { SignIn, SignUp, useAuth } from './services/clerkReact';
import NotFoundPage from './pages/NotFoundPage';
import { CommandPalette } from './components/ui/command-palette';
import { useOnboardingState } from './hooks/useOnboarding';
import { flags } from './config/flags';

const HomePage = lazy(() => import('./pages/HomePage'));
const TaxPage = lazy(() => import('./pages/TaxPage'));
const DeductionsPage = lazy(() => import('./pages/DeductionsPage'));
const Form1099Page = lazy(() => import('./pages/Form1099Page'));
const ContractorsPage = lazy(() => import('./pages/ContractorsPage'));
const AccountantAcceptPage = lazy(() => import('./pages/AccountantAcceptPage'));
const RetirementPage = lazy(() => import('./pages/RetirementPage'));
const NetWorthPage = lazy(() => import('./pages/NetWorthPage'));
const DebtsPage = lazy(() => import('./pages/DebtsPage'));
const PlanPage = lazy(() => import('./pages/PlanPage'));
const HousePage = lazy(() => import('./pages/HousePage'));
const KidsPage = lazy(() => import('./pages/KidsPage'));
const IncomeStreamsPage = lazy(() => import('./pages/IncomeStreamsPage'));
const CashPage = lazy(() => import('./pages/CashPage'));
const RevenuePage = lazy(() => import('./pages/RevenuePage'));
const ExpensesPage = lazy(() => import('./pages/ExpensesPage'));
const AdminAnalyticsDashboard = lazy(() => import('./pages/AdminAnalyticsDashboard'));
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'));
const RulesPage = lazy(() => import('./components/rules/RulesPage'));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const AccountsPage = lazy(() => import('./pages/AccountsPage'));
const BalanceSheetPage = lazy(() => import('./pages/BalanceSheetPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const FinancialStatementsPage = lazy(
  () => import('./pages/FinancialStatementsPage'),
);
const ForecastingPage = lazy(() => import('./pages/ForecastingPage'));
const BudgetsPage = lazy(() => import('./pages/BudgetsPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const BankConnectionsPage = lazy(() => import('./pages/BankConnectionsPage'));
const PaystubsPage = lazy(() => import('./pages/PaystubsPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const GoalsPage = lazy(() => import('./pages/GoalsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));

const normalizeOrigin = (value: string | undefined) => {
  if (!value) return undefined;
  try {
    return new URL(value).origin;
  } catch {
    return value.replace(/\/+$/, '');
  }
};

const appOrigin = normalizeOrigin(import.meta.env.VITE_APP_URL);
const landingOrigin = normalizeOrigin(import.meta.env.VITE_LANDING_URL);

const appHomeUrl = appOrigin ? `${appOrigin}/home` : '/home';
const landingRootUrl = landingOrigin ?? '/';

const sameOrigin = appOrigin && landingOrigin ? appOrigin === landingOrigin : false;

const resolveSurface = () => {
  if (typeof window === 'undefined') {
    return 'app';
  }

  const currentOrigin = window.location.origin;
  const path = window.location.pathname;

  if (!appOrigin || !landingOrigin) {
    return /^\/(home|dashboard|admin|rules|transactions|accounts|balance-sheet|forecasting|budgets|invoices|bank-connections|paystubs|integrations|tax|cash|revenue|expenses|onboarding|goals|settings|sign-)/.test(path) ? 'app' : 'landing';
  }

  if (sameOrigin) {
    if (/^\/(home|dashboard|admin|rules|transactions|accounts|balance-sheet|forecasting|budgets|invoices|bank-connections|paystubs|integrations|tax|cash|revenue|expenses|onboarding|goals|settings|sign-)/.test(path)) {
      return 'app';
    }
    return 'landing';
  }

  if (currentOrigin === appOrigin) {
    return 'app';
  }

  if (currentOrigin === landingOrigin) {
    return 'landing';
  }

  // Fallback: infer surface from the requested path when origins are mismatched
  return /^\/(home|dashboard|admin|rules|transactions|accounts|balance-sheet|forecasting|budgets|invoices|bank-connections|paystubs|integrations|tax|cash|revenue|expenses|onboarding|goals|settings|sign-)/.test(path) ? 'app' : 'landing';
};

const AuthLayout = ({ children }: { children: ReactNode }) => (
  <div className="flex min-h-screen items-center justify-center bg-bg-secondary px-4 py-12">
    <div className="w-full max-w-md">{children}</div>
  </div>
);

const Spinner = ({ label }: { label: string }) => (
  <div className="flex min-h-screen items-center justify-center bg-bg-secondary text-fg-secondary">
    <div className="flex items-center gap-3 rounded-xl border border-border bg-bg-primary/80 px-6 py-4 shadow-sm">
      <Clock className="h-5 w-5 animate-spin text-forest" />
      <span className="text-sm font-medium">{label}</span>
    </div>
  </div>
);

// Genuine cross-origin / auth redirects.
const RedirectSpinner = () => <Spinner label="Redirecting…" />;
// Lazy route chunk loading — a page is being fetched, not a redirect.
const LoadingSpinner = () => <Spinner label="Loading…" />;

function ExternalRedirect({ href }: { href: string }) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const target = new URL(href, window.location.href);
      if (target.href === window.location.href) {
        return;
      }
      window.location.assign(target.href);
    } catch {
      window.location.assign(href);
    }
  }, [href]);

  return <RedirectSpinner />;
}

/**
 * AuthGuard waits for Clerk to finish loading before deciding what to render.
 * This prevents the redirect from firing during the brief loading window
 * when isLoaded is false and isSignedIn is undefined.
 */
function AuthGuard({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return <RedirectSpinner />;
  }

  if (!isSignedIn) {
    return sameOrigin ? <Navigate to="/sign-in" replace /> : <ExternalRedirect href={landingRootUrl} />;
  }

  return <>{children}</>;
}

/**
 * OnboardingGuard runs AFTER AuthGuard. If the active org has not
 * completed onboarding, redirect to /onboarding. Falls through silently
 * on API errors (new orgs without memberships, network issues) so the
 * app stays usable.
 */
function OnboardingGuard({ children }: { children: ReactNode }) {
  const { data, isLoading, isError } = useOnboardingState();

  if (isLoading) {
    return <RedirectSpinner />;
  }
  if (!isError && data && !data.isCompleted) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

const AppHomeRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <HomePage />
    </OnboardingGuard>
  </AuthGuard>
);

const OnboardingRoute = () => (
  <AuthGuard>
    <OnboardingPage />
  </AuthGuard>
);

const AdminRoute = () => (
  <AuthGuard>
    <AdminAnalyticsDashboard />
  </AuthGuard>
);

const RulesRoute = () => (
  <AuthGuard>
    <RulesPage />
  </AuthGuard>
);

const TransactionsRoute = () => (
  <AuthGuard>
    <TransactionsPage />
  </AuthGuard>
);

// C2 · W3 — the keyboard-first Review queue. Route is registered only when
// flags.review is on (flag-off = no /review today).
const ReviewRoute = () => (
  <AuthGuard>
    <ReviewPage />
  </AuthGuard>
);

const AccountsRoute = () => (
  <AuthGuard>
    <AccountsPage />
  </AuthGuard>
);

const BalanceSheetRoute = () => (
  <AuthGuard>
    <BalanceSheetPage />
  </AuthGuard>
);

const ReportsRoute = () => (
  <AuthGuard>
    <ReportsPage />
  </AuthGuard>
);

const FinancialStatementsRoute = () => (
  <AuthGuard>
    <FinancialStatementsPage />
  </AuthGuard>
);

const ForecastingRoute = () => (
  <AuthGuard>
    <ForecastingPage />
  </AuthGuard>
);

const BudgetsRoute = () => (
  <AuthGuard>
    <BudgetsPage />
  </AuthGuard>
);

const InvoicesRoute = () => (
  <AuthGuard>
    <InvoicesPage />
  </AuthGuard>
);

const BankConnectionsRoute = () => (
  <AuthGuard>
    <BankConnectionsPage />
  </AuthGuard>
);

const PaystubsRoute = () => (
  <AuthGuard>
    <PaystubsPage />
  </AuthGuard>
);

const IntegrationsRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <IntegrationsPage />
    </OnboardingGuard>
  </AuthGuard>
);

const TaxRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <TaxPage />
    </OnboardingGuard>
  </AuthGuard>
);

const DeductionsRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <DeductionsPage />
    </OnboardingGuard>
  </AuthGuard>
);

const ContractorsRoute = () => (
  <AuthGuard>
    <ContractorsPage />
  </AuthGuard>
);

const Form1099Route = () => (
  <AuthGuard>
    <OnboardingGuard>
      <Form1099Page />
    </OnboardingGuard>
  </AuthGuard>
);

const RetirementRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <RetirementPage />
    </OnboardingGuard>
  </AuthGuard>
);

const NetWorthRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <NetWorthPage />
    </OnboardingGuard>
  </AuthGuard>
);

const DebtsRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <DebtsPage />
    </OnboardingGuard>
  </AuthGuard>
);

const PlanRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <PlanPage />
    </OnboardingGuard>
  </AuthGuard>
);

const HouseRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <HousePage />
    </OnboardingGuard>
  </AuthGuard>
);

const KidsRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <KidsPage />
    </OnboardingGuard>
  </AuthGuard>
);

const IncomeStreamsRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <IncomeStreamsPage />
    </OnboardingGuard>
  </AuthGuard>
);

const CashRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <CashPage />
    </OnboardingGuard>
  </AuthGuard>
);

const RevenueRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <RevenuePage />
    </OnboardingGuard>
  </AuthGuard>
);

const ExpensesRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <ExpensesPage />
    </OnboardingGuard>
  </AuthGuard>
);

const GoalsRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <GoalsPage />
    </OnboardingGuard>
  </AuthGuard>
);

const SettingsRoute = () => (
  <AuthGuard>
    <OnboardingGuard>
      <SettingsPage />
    </OnboardingGuard>
  </AuthGuard>
);

const LandingHomeRoute = () => <WaitlistPage />;

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/home" element={<AppHomeRoute />} />
      <Route path="/dashboard" element={<Navigate to="/home" replace />} />
      <Route path="/rules" element={<RulesRoute />} />
      <Route path="/transactions" element={<TransactionsRoute />} />
      {flags.review && <Route path="/review" element={<ReviewRoute />} />}
      <Route path="/accounts" element={<AccountsRoute />} />
      <Route path="/balance-sheet" element={<BalanceSheetRoute />} />
      <Route path="/reports" element={<ReportsRoute />} />
      <Route
        path="/financial-statements"
        element={<FinancialStatementsRoute />}
      />
      <Route path="/forecasting" element={<ForecastingRoute />} />
      <Route path="/budgets" element={<BudgetsRoute />} />
      <Route path="/invoices" element={<InvoicesRoute />} />
      <Route path="/bank-connections" element={<BankConnectionsRoute />} />
      <Route path="/paystubs" element={<PaystubsRoute />} />
      <Route path="/integrations" element={<IntegrationsRoute />} />
      <Route path="/tax" element={<TaxRoute />} />
      <Route path="/deductions" element={<DeductionsRoute />} />
      <Route path="/form-1099" element={<Form1099Route />} />
      <Route path="/contractors" element={<ContractorsRoute />} />
      <Route
        path="/accountant/accept-invite/:token"
        element={
          <AuthGuard>
            <AccountantAcceptPage />
          </AuthGuard>
        }
      />
      <Route path="/retirement" element={<RetirementRoute />} />
      <Route path="/net-worth" element={<NetWorthRoute />} />
      <Route path="/debts" element={<DebtsRoute />} />
      <Route path="/plan" element={<PlanRoute />} />
      <Route path="/house" element={<HouseRoute />} />
      <Route path="/kids" element={<KidsRoute />} />
      <Route path="/income-streams" element={<IncomeStreamsRoute />} />
      <Route path="/cash" element={<CashRoute />} />
      <Route path="/revenue" element={<RevenueRoute />} />
      <Route path="/expenses" element={<ExpensesRoute />} />
      <Route path="/goals" element={<GoalsRoute />} />
      <Route path="/settings" element={<SettingsRoute />} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/admin/analytics" element={<AdminRoute />} />
      {/* Auth UI always lives on the app domain — never redirect away from here */}
      <Route
        path="/sign-in/*"
        element={
          <AuthLayout>
            <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" afterSignInUrl={appHomeUrl} />
          </AuthLayout>
        }
      />
      <Route
        path="/sign-up/*"
        element={
          <AuthLayout>
            <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" afterSignUpUrl={appHomeUrl} />
          </AuthLayout>
        }
      />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

// Compute app-domain auth URLs once (used by LandingRoutes redirects)
const appSignInUrl = appOrigin ? `${appOrigin}/sign-in` : '/sign-in';
const appSignUpUrl = appOrigin ? `${appOrigin}/sign-up` : '/sign-up';

function LandingRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingHomeRoute />} />
      {/*
        Auth UI belongs on the app domain where Clerk is authorised to load.
        Redirect any landing /sign-in or /sign-up visits straight to the app
        domain so Clerk's <SignIn> / <SignUp> components can render correctly.
      */}
      <Route path="/sign-in/*" element={<ExternalRedirect href={appSignInUrl} />} />
      <Route path="/sign-up/*" element={<ExternalRedirect href={appSignUpUrl} />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  const surface = useMemo(() => resolveSurface(), []);

  return (
    <div className="bg-bg-secondary text-fg-primary min-h-screen font-sans">
      <Suspense fallback={<LoadingSpinner />}>
        {surface === 'landing' ? <LandingRoutes /> : <AppRoutes />}
      </Suspense>
      {/* ⌘K command palette — mounted once at the app root (A14). */}
      {surface !== 'landing' && <CommandPalette />}
    </div>
  );
}

export default App;
